<template>
  <q-dialog
    v-model="isVisible"
    persistent
    transition-show="scale"
    transition-hide="scale"
  >
    <q-card class="detect-damage-modal">
      <q-card-section class="detect-damage-modal__header">
        <!-- Inline rather than an asset: two paths, and it is the only place in
             the app that draws the sparkle. -->
        <svg
          class="detect-damage-modal__sparkle"
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M13.5 3.5L15.6 10.4L22.5 12.5L15.6 14.6L13.5 21.5L11.4 14.6L4.5 12.5L11.4 10.4L13.5 3.5Z"
            fill="#FFD966"
          />
          <path
            d="M25.5 18.5L26.7 22.3L30.5 23.5L26.7 24.7L25.5 28.5L24.3 24.7L20.5 23.5L24.3 22.3L25.5 18.5Z"
            fill="#FFD966"
          />
        </svg>
        <h3 class="detect-damage-modal__title">
          {{ $t("damageDetection.title") }}
        </h3>
        <span class="detect-damage-modal__beta">
          {{ $t("damageDetection.beta") }}
        </span>
      </q-card-section>

      <q-card-section class="detect-damage-modal__body">
        {{
          noDamagesFound
            ? $t("damageDetection.noDamages")
            : $t("damageDetection.body")
        }}
      </q-card-section>

      <!-- Once the scan has answered there is nothing left to confirm or call
           off, so the two choices collapse into acknowledging the answer. -->
      <q-card-actions class="detect-damage-modal__actions">
        <button
          v-if="!noDamagesFound"
          class="detect-damage-modal__button detect-damage-modal__button--ghost"
          @click="$emit('cancel')"
        >
          {{ $t("damageDetection.cancel") }}
        </button>
        <button
          v-if="noDamagesFound"
          class="detect-damage-modal__button detect-damage-modal__button--primary"
          @click="$emit('cancel')"
        >
          {{ $t("damageDetection.close") }}
        </button>
        <button
          v-else
          class="detect-damage-modal__button detect-damage-modal__button--primary"
          :disabled="loading"
          @click="$emit('confirm')"
        >
          <q-spinner v-if="loading" size="18px" class="q-mr-sm" />
          {{ $t("damageDetection.confirm") }}
        </button>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import {computed} from "vue";
import {QSpinner} from "quasar";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  // True while the opt-in is being written and the damages fetched, so the
  // dialog stays open instead of flashing an empty sidebar.
  loading: {
    type: Boolean,
    default: false,
  },
  // The scan ran and turned up nothing. The dialog stays up to say so — closing
  // it onto an empty sidebar reads as the feature having failed.
  noDamagesFound: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});
</script>

<style lang="scss" scoped>
.detect-damage-modal {
  min-width: 420px;
  max-width: 620px;
  width: 90vw;
  background-color: var(--color-dark-bg-primary);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  color: white;
  font-family: "Roboto";

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 28px 32px 8px 32px;
  }

  &__sparkle {
    flex-shrink: 0;
  }

  &__title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
  }

  &__beta {
    margin-left: 8px;
    padding: 6px 14px;
    border: 1px solid var(--color-gree300);
    border-radius: 6px;
    color: var(--color-gree300);
    font-size: 15px;
    font-weight: 500;
    white-space: nowrap;
  }

  &__body {
    padding: 12px 32px 8px 32px;
    font-size: 16px;
    line-height: 1.6;
    color: #e6e6e6;
  }

  &__actions {
    padding: 20px 32px 28px 32px;
    justify-content: flex-end;
    gap: 16px;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;

    &--ghost {
      background: transparent;
      border: 1px solid var(--color-dark-bg-tertiary, #5a5a5a);
      color: white;

      &:hover {
        border-color: white;
      }
    }

    &--primary {
      background: var(--color-gree300);
      border: 1px solid var(--color-gree300);
      color: var(--color-dark-bg-primary);

      &:hover:not(:disabled) {
        background: var(--color-gree200);
        border-color: var(--color-gree200);
      }

      &:disabled {
        opacity: 0.6;
        cursor: default;
      }
    }
  }
}
</style>
